/**
 * Transforms a display name string by applying formatting rules:
 * - Removes duplicate consecutive words
 * - Always capitalizes short words (1-2 characters)
 * - Converts words that start with lowercase and are > 2 characters to capital case
 * - Preserves all-caps words (> 2 chars)
 * - Preserves special characters and parentheses
 * @param displayName - The display name string to transform
 * @returns Transformed display name string
 * @example
 * transformDisplayName("E RIDE PRO PRO Ss X-s (16\")") // Returns "E RIDE PRO SS X-S (16\")"
 */
const transformDisplayName = (displayName) => {
    if (!displayName?.trim()) {
        return displayName;
    }
    // Split by whitespace but preserve the structure
    // We need to handle words that might have special characters
    const words = displayName.trim().split(/\s+/).filter(Boolean);
    // Remove duplicate consecutive words (case-insensitive)
    const deduplicatedWords = [];
    const originalWords = []; // Track original words for comparison
    for (let i = 0; i < words.length; i++) {
        const currentWord = words[i];
        const previousOriginalWord = originalWords[originalWords.length - 1];
        // Normalize for comparison (remove special chars, lowercase)
        const currentWordNormalized = currentWord.replace(/[^\w]/g, '').toLowerCase();
        const previousWordNormalized = previousOriginalWord
            ? previousOriginalWord.replace(/[^\w]/g, '').toLowerCase()
            : '';
        // First check: exact duplicate (e.g., "Pro" and "Pro")
        if (currentWordNormalized && currentWordNormalized === previousWordNormalized) {
            // Skip duplicate
            continue;
        }
        // Second check: current word starts with previous word (handles "Pro Pro-S" -> "Pro S")
        if (previousWordNormalized && currentWordNormalized) {
            // Extract the alphanumeric prefix of current word
            const currentWordAlphanumericPrefix = currentWord.match(/^([a-zA-Z0-9]+)/)?.[1] || '';
            const currentWordPrefixNormalized = currentWordAlphanumericPrefix.toLowerCase();
            // If the alphanumeric prefix of current word matches the previous word, remove the duplicate
            if (currentWordPrefixNormalized === previousWordNormalized &&
                currentWordAlphanumericPrefix.length > 0) {
                // Remove the duplicate prefix and keep the rest
                let remainingPart = currentWord.slice(currentWordAlphanumericPrefix.length);
                // Trim leading special characters (like hyphens) to avoid "Pro -S", we want "Pro S"
                remainingPart = remainingPart.replace(/^[^\w]+/, '');
                if (remainingPart.trim()) {
                    // Process the remaining part as a new word
                    words.splice(i, 1, remainingPart);
                    i--; // Re-process this word
                    continue;
                }
                else {
                    // Skip if no remaining part
                    continue;
                }
            }
        }
        // Transform word based on length and casing
        let transformedWord = currentWord;
        const alphanumericContent = currentWord.replace(/[^\w]/g, '');
        const firstLetterMatch = currentWord.match(/^[^\w]*([a-zA-Z])/);
        if (firstLetterMatch) {
            const firstLetter = firstLetterMatch[1];
            // Always capitalize short words (1-2 characters) to uppercase
            if (alphanumericContent.length <= 2) {
                // Split into segments to preserve special characters
                const segments = currentWord.split(/([a-zA-Z]+|\d+|[^\w])/g).filter(Boolean);
                const transformedSegments = segments.map((segment) => {
                    if (/^[a-zA-Z]+$/.test(segment)) {
                        // Convert all letters to uppercase for short words
                        return segment.toUpperCase();
                    }
                    // Keep digits and non-alphanumeric characters as-is
                    return segment;
                });
                transformedWord = transformedSegments.join('');
            }
            // If first letter is lowercase and word is > 2 characters, convert to capital case
            else if (firstLetter === firstLetter.toLowerCase()) {
                // Split into segments to preserve special characters
                const segments = currentWord.split(/([a-zA-Z]+|\d+|[^\w])/g).filter(Boolean);
                const transformedSegments = segments.map((segment) => {
                    if (/^[a-zA-Z]+$/.test(segment)) {
                        // Convert to capital case: first letter uppercase, rest lowercase
                        return segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase();
                    }
                    // Keep digits and non-alphanumeric characters as-is
                    return segment;
                });
                transformedWord = transformedSegments.join('');
            }
        }
        deduplicatedWords.push(transformedWord);
        originalWords.push(currentWord); // Track original for next iteration comparison
    }
    return deduplicatedWords.join(' ');
};

export { transformDisplayName };
//# sourceMappingURL=displayNameTransform.js.map
